// Recibo de pagamento — reimpressão da baixa, pra mandar pro cliente.
//
// Segue o fluxo do próprio Microuniverso, que é o que a crediarista já conhece:
// escolhe a LOJA e a DATA, vê os recibos daquele dia e escolhe qual quer.
//
// Antes de mandar, ela CONFERE. O preview é HTML e não o PDF embutido: o Chrome
// do Android — o navegador do tablet — não renderiza PDF dentro de iframe, e ela
// ficaria olhando um retângulo cinza achando que quebrou. Quem quiser ver o
// papel exato abre o PDF num toque.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API } from '../contexts/AuthContext';
import backdropClose from '../helpers/backdropClose';
import { baixarArquivo, noApp } from '../helpers/appNativo';

const VERDE = '#3D8B37';

const dinheiro = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dia = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '');
const hoje = () => new Date().toISOString().slice(0, 10);

// Preview: os mesmos dados que vão no papel, na ordem do papel.
function Preview({ r }) {
  return (
    <div style={{
      border: '1px solid var(--portal-border)', borderRadius: 10, padding: 14,
      background: 'var(--portal-bg)', display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)' }}>
        {r.filial?.empresa}
        {r.filial?.cnpj ? ` · CNPJ ${r.filial.cnpj}` : ''}
        {r.filial?.nome ? ` · Loja ${r.filial.filial} — ${r.filial.nome}` : ''}
      </div>

      <div>
        <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)' }}>Recebemos de</div>
        <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{r.cliente?.nome}</div>
      </div>

      <div style={{
        background: 'rgba(61,139,55,0.10)', borderRadius: 8, padding: '10px 12px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      }}>
        <span>
          <span style={{ fontSize: '0.7rem', color: 'var(--portal-muted)', display: 'block' }}>Valor pago</span>
          <strong style={{ fontSize: '1.25rem', color: VERDE }}>R$ {dinheiro(r.totais?.total)}</strong>
        </span>
        <span style={{ fontSize: '0.75rem', color: 'var(--portal-muted)', textAlign: 'right' }}>
          {(r.formas || []).map((f) => `${f.forma} ${dinheiro(f.valor)}`).join(' · ')}
        </span>
      </div>

      <div style={{ maxHeight: 190, overflowY: 'auto' }}>
        <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: 'var(--portal-muted)', textAlign: 'left' }}>
              <th style={{ padding: '3px 0', fontWeight: 600 }}>Documento</th>
              <th style={{ padding: '3px 0', fontWeight: 600 }}>Vencimento</th>
              <th style={{ padding: '3px 0', fontWeight: 600, textAlign: 'right' }}>Devido</th>
              <th style={{ padding: '3px 0', fontWeight: 600, textAlign: 'right' }}>Pago</th>
            </tr>
          </thead>
          <tbody>
            {(r.titulos || []).map((t) => (
              <tr key={t.documento} style={{ borderTop: '1px solid var(--portal-border)' }}>
                <td style={{ padding: '4px 0' }}>
                  {t.documento}
                  {!t.quitado && <span style={{ color: 'var(--portal-muted)' }}> · parcial</span>}
                </td>
                <td style={{ padding: '4px 0' }}>{dia(t.vencimento)}</td>
                <td style={{ padding: '4px 0', textAlign: 'right' }}>{dinheiro(t.devido)}</td>
                {/* O mesmo número que sai no PDF: o valor, sem somar o juros —
                    senão o preview confere uma coisa e o papel mostra outra. */}
                <td style={{ padding: '4px 0', textAlign: 'right' }}>{dinheiro(t.valor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
        Valor por extenso: {r.extenso}
      </div>
    </div>
  );
}

export default function ReciboModal({ aberto, oid, cliente, conversaId, onClose }) {
  const [filiais, setFiliais] = useState([]);
  const [filial, setFilial] = useState('');
  const [de, setDe] = useState(hoje);
  const [ate, setAte] = useState(hoje);
  const [lista, setLista] = useState(null); // null = ainda não buscou
  const [buscando, setBuscando] = useState(false);
  const [escolhido, setEscolhido] = useState(null); // recibo montado (preview)
  const [ocupado, setOcupado] = useState('');
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (!aberto) return;
    setLista(null); setEscolhido(null); setErro('');
    axios.get(`${API}/consulta-cliente/recibos/filiais`)
      .then((r) => setFiliais(r.data?.data?.filiais || []))
      .catch(() => setFiliais([]));
  }, [aberto]);

  const buscar = useCallback(async () => {
    if (!filial) { setErro('Escolha a loja.'); return; }
    setBuscando(true); setErro(''); setEscolhido(null);
    try {
      const r = await axios.get(`${API}/consulta-cliente/${oid}/recibos`, { params: { filial, de, ate } });
      setLista(r.data?.data?.recibos || []);
    } catch (e) {
      setErro(e?.response?.data?.message || 'Não deu pra buscar os recibos.');
      setLista([]);
    } finally { setBuscando(false); }
  }, [oid, filial, de, ate]);

  const abrirPreview = useCallback(async (ato) => {
    setOcupado('preview'); setErro('');
    try {
      const r = await axios.get(`${API}/consulta-cliente/${oid}/recibos/${ato}`);
      setEscolhido(r.data?.data || null);
    } catch (e) {
      setErro('Não deu pra montar o recibo.');
    } finally { setOcupado(''); }
  }, [oid]);

  const abrirPdf = useCallback(async () => {
    setOcupado('pdf');
    try {
      const r = await axios.get(`${API}/consulta-cliente/${oid}/recibos/${escolhido.ato}/pdf`, { responseType: 'blob' });
      const pdf = new Blob([r.data], { type: 'application/pdf' });
      // No app não existe "abrir numa aba": o arquivo vai pro visualizador do
      // celular. Sem este desvio ele até abriria (a ponte intercepta o
      // `window.open` de um blob), mas chegaria chamado de "documento.pdf" —
      // e recibo com nome genérico é o que se perde na pasta de downloads.
      if (noApp()) {
        await baixarArquivo(`recibo-${String(cliente?.nome || 'cliente').toLowerCase().replace(/\s+/g, '-')}-${escolhido.ato}.pdf`, pdf);
        return;
      }
      const url = window.URL.createObjectURL(pdf);
      window.open(url, '_blank');
      // Não revoga na hora: a aba nova ainda está carregando o blob.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setErro('Não deu pra abrir o PDF.');
    } finally { setOcupado(''); }
  }, [oid, escolhido, cliente]);

  // Mandar é irreversível — cai no WhatsApp do cliente na hora. Confirma
  // mostrando valor e data, que é onde um recibo trocado apareceria.
  const enviar = useCallback(async () => {
    const { isConfirmed, value } = await Swal.fire({
      icon: 'question',
      title: 'Enviar o recibo?',
      html: `Vai pro WhatsApp desta conversa.<br><b>R$ ${dinheiro(escolhido?.totais?.total)}</b>`
        + ` · ${dia(escolhido?.data)}${escolhido?.numero ? ` · recibo ${escolhido.numero}` : ''}`,
      input: 'text',
      inputLabel: 'Mensagem junto (opcional)',
      inputPlaceholder: 'Ex.: segue o comprovante do seu pagamento',
      showCancelButton: true,
      confirmButtonText: 'Enviar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: VERDE,
    });
    if (!isConfirmed) return;
    setOcupado('enviar'); setErro('');
    try {
      await axios.post(`${API}/consulta-cliente/${oid}/recibos/${escolhido.ato}/enviar`, {
        conversa_id: conversaId, texto: value || '',
      });
      onClose();
      Swal.fire({ icon: 'success', title: 'Recibo enviado', timer: 1600, showConfirmButton: false });
    } catch (e) {
      setErro(e?.response?.data?.message || 'Não deu pra enviar o recibo.');
    } finally { setOcupado(''); }
  }, [oid, conversaId, escolhido, onClose]);

  if (!aberto) return null;

  return (
    <div
      {...backdropClose(onClose)}
      style={{
        position: 'fixed', inset: 0, zIndex: 20045, background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--portal-surface)', color: 'var(--portal-text)',
          border: '1px solid var(--portal-border)', borderRadius: 12, padding: 16,
          width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div>
            <div style={{ fontWeight: 700 }}>Recibo de pagamento</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>{cliente?.nome}</div>
          </div>
          <button
            type="button" onClick={onClose} title="Fechar" aria-label="Fechar"
            style={{ background: 'transparent', border: 'none', color: 'var(--portal-text)', fontSize: '1.1rem', cursor: 'pointer' }}
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {/* Loja e data, como no ERP: o recibo é de uma baixa, feita numa loja,
            num dia. Sem isso a lista viria de todas as lojas misturadas. */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 150px', fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
            Loja
            <select className="form-select form-select-sm" value={filial} onChange={(e) => setFilial(e.target.value)}>
              <option value="">escolha…</option>
              {filiais.map((f) => <option key={f.filial} value={f.filial}>{f.filial} — {f.nome}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 110px', fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
            De
            <input type="date" className="form-control form-control-sm" value={de}
              onChange={(e) => { setDe(e.target.value); if (e.target.value > ate) setAte(e.target.value); }} />
          </label>
          <label style={{ flex: '1 1 110px', fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
            Até
            <input type="date" className="form-control form-control-sm" value={ate}
              onChange={(e) => setAte(e.target.value)} />
          </label>
          <button
            type="button" onClick={buscar} disabled={buscando}
            style={{
              background: VERDE, color: '#fff', border: 'none', borderRadius: 8,
              padding: '7px 16px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
            }}
          >
            {buscando ? 'Buscando…' : 'Buscar'}
          </button>
        </div>

        {erro && <div style={{ color: '#e0736f', fontSize: '0.82rem' }}>{erro}</div>}

        {lista !== null && !lista.length && !buscando && (
          <div style={{ color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
            Nenhum pagamento desse cliente nessa loja e nesse período.
          </div>
        )}

        {lista !== null && lista.length > 0 && !escolhido && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {lista.map((r) => (
              <button
                key={r.ato}
                type="button"
                onClick={() => abrirPreview(r.ato)}
                disabled={ocupado === 'preview'}
                style={{
                  textAlign: 'left', background: 'var(--portal-bg)', color: 'var(--portal-text)',
                  border: '1px solid var(--portal-border)', borderRadius: 8, padding: '9px 12px',
                  cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', gap: 10,
                }}
              >
                <span>
                  <span style={{ fontWeight: 600, fontSize: '0.88rem', display: 'block' }}>
                    R$ {dinheiro(r.total)}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
                    {dia(r.data)}
                    {r.numero ? ` · recibo ${r.numero}` : ''}
                    {r.formas?.length ? ` · ${r.formas.map((f) => f.forma).join(', ')}` : ''}
                  </span>
                </span>
                <i className="bi bi-chevron-right" style={{ color: 'var(--portal-muted)' }} />
              </button>
            ))}
          </div>
        )}

        {escolhido && (
          <>
            <button
              type="button"
              onClick={() => setEscolhido(null)}
              style={{
                background: 'transparent', border: 'none', color: 'var(--portal-muted)',
                padding: 0, fontSize: '0.8rem', cursor: 'pointer', alignSelf: 'flex-start',
                display: 'flex', alignItems: 'center', gap: 5,
              }}
            >
              <i className="bi bi-chevron-left" /> outros recibos
            </button>

            <Preview r={escolhido} />

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button" onClick={abrirPdf} disabled={!!ocupado}
                style={{
                  flex: '0 0 auto', background: 'transparent', color: 'var(--portal-text)',
                  border: '1px solid var(--portal-border)', borderRadius: 8,
                  padding: '9px 14px', fontSize: '0.85rem', cursor: 'pointer',
                }}
              >
                <i className="bi bi-file-earmark-pdf me-1" />
                {ocupado === 'pdf' ? 'Abrindo…' : 'Ver PDF'}
              </button>
              <button
                type="button" onClick={enviar} disabled={!!ocupado}
                style={{
                  flex: 1, background: VERDE, color: '#fff', border: 'none', borderRadius: 8,
                  padding: '9px 0', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer',
                }}
              >
                {ocupado === 'enviar' ? 'Enviando…' : 'Enviar pro cliente'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
